import { useEffect, useRef, useCallback } from 'react';
import './EarthquakeAnimation.css';

/**
 * EARTHQUAKE ANIMATION - Ground-type Move
 * No sprite images - pure screen and Pokémon shaking effects
 * 
 * Animation Sequence:
 * 1. PREPARATION: Player Pokémon preparation stance
 * 2. RUMBLE_START: Initial ground tremors begin
 * 3. EARTHQUAKE_MAIN: Full earthquake with intense shaking
 * 4. AFTERSHOCK: Diminishing tremors
 * 5. SETTLE: Everything settles back to normal
 * 
 * Effects: Screen shake, both Pokémon movement, ground-type visual effects
 */
export default function EarthquakeAnimation({ playerRef, opponentRef, onComplete }) {
  const timersRef = useRef([]);
  const originalStylesRef = useRef({});

  // Cleanup function
  const cleanup = useCallback(() => {
    // Clear all timers
    timersRef.current.forEach(timer => clearTimeout(timer));
    timersRef.current = [];
    
    // Reset player styles
    if (playerRef.current && originalStylesRef.current.player) {
      playerRef.current.style.transition = originalStylesRef.current.player.transition || '';
      playerRef.current.style.transform = originalStylesRef.current.player.transform || '';
      playerRef.current.style.filter = originalStylesRef.current.player.filter || '';
      playerRef.current.classList.remove('earthquake-shake', 'earthquake-hit');
    }
    
    // Reset opponent styles
    if (opponentRef.current && originalStylesRef.current.opponent) {
      opponentRef.current.style.transition = originalStylesRef.current.opponent.transition || '';
      opponentRef.current.style.transform = originalStylesRef.current.opponent.transform || '';
      opponentRef.current.style.filter = originalStylesRef.current.opponent.filter || '';
      opponentRef.current.classList.remove('earthquake-shake', 'earthquake-hit');
    }
    
    // Reset screen shake from all possible containers
    const battleContainer = playerRef.current?.closest('.battle-container') || 
                           playerRef.current?.closest('.battle-scene') || 
                           playerRef.current?.closest('.game-container') || 
                           playerRef.current?.parentElement || 
                           document.body;
    
    const battleScene = document.querySelector('.battle-scene') || 
                       document.querySelector('.battle-container') || 
                       document.querySelector('.game-container') || 
                       battleContainer;
    
    battleContainer.classList.remove('earthquake-screen-shake');
    if (battleScene !== battleContainer) {
      battleScene.classList.remove('earthquake-screen-shake');
    }
    
    const parentContainer = battleContainer.parentElement;
    if (parentContainer && parentContainer !== document.body) {
      parentContainer.classList.remove('earthquake-screen-shake');
    }
    
    // Remove fallback body shake
    document.body.classList.remove('earthquake-screen-shake');
    
    // Remove fallback styles
    const fallbackStyle = document.getElementById('earthquake-fallback-shake');
    if (fallbackStyle) {
      fallbackStyle.remove();
    }
  }, [playerRef, opponentRef]);

  // Store original styles
  const storeOriginalStyles = useCallback(() => {
    if (playerRef.current) {
      originalStylesRef.current.player = {
        transition: playerRef.current.style.transition,
        transform: playerRef.current.style.transform,
        filter: playerRef.current.style.filter,
      };
    }
    
    if (opponentRef.current) {
      originalStylesRef.current.opponent = {
        transition: opponentRef.current.style.transition,
        transform: opponentRef.current.style.transform,
        filter: opponentRef.current.style.filter,
      };
    }
  }, [playerRef, opponentRef]);

  // Create ground crack visual effects
  const createGroundEffects = useCallback(() => {
    if (!opponentRef.current) return;
    
    const container = opponentRef.current.parentElement;
    if (!container) return;
    
    // Create multiple ground crack lines
    for (let i = 0; i < 5; i++) {
      const crack = document.createElement('div');
      crack.className = 'earthquake-ground-crack';
      
      // Random positioning across the battle field
      const randomX = Math.random() * 80 + 10; // 10-90% width
      const randomY = Math.random() * 60 + 20; // 20-80% height
      const randomRotation = Math.random() * 60 - 30; // -30 to 30 degrees
      const randomLength = Math.random() * 40 + 60; // 60-100px length
      
      crack.style.left = `${randomX}%`;
      crack.style.top = `${randomY}%`;
      crack.style.transform = `translate(-50%, -50%) rotate(${randomRotation}deg)`;
      crack.style.width = `${randomLength}px`;
      crack.style.setProperty('--delay', `${i * 100}ms`);
      
      container.appendChild(crack);
      
      // Remove crack after animation
      const removeTimer = setTimeout(() => {
        if (crack.parentNode) {
          crack.parentNode.removeChild(crack);
        }
      }, 2000);
      
      timersRef.current.push(removeTimer);
    }
  }, [opponentRef]);

  // Create dust cloud effects
  const createDustClouds = useCallback(() => {
    if (!opponentRef.current) return;
    
    const container = opponentRef.current.parentElement;
    if (!container) return;
    
    // Create multiple dust clouds
    for (let i = 0; i < 8; i++) {
      const dust = document.createElement('div');
      dust.className = 'earthquake-dust-cloud';
      
      // Random positioning
      const randomX = Math.random() * 90 + 5; // 5-95% width
      const randomY = Math.random() * 70 + 15; // 15-85% height
      const randomScale = Math.random() * 0.5 + 0.5; // 0.5-1.0 scale
      
      dust.style.left = `${randomX}%`;
      dust.style.top = `${randomY}%`;
      dust.style.transform = `translate(-50%, -50%) scale(${randomScale})`;
      dust.style.setProperty('--delay', `${i * 150}ms`);
      
      container.appendChild(dust);
      
      // Remove dust after animation
      const removeTimer = setTimeout(() => {
        if (dust.parentNode) {
          dust.parentNode.removeChild(dust);
        }
      }, 3000);
      
      timersRef.current.push(removeTimer);
    }
  }, [opponentRef]);

  // Main animation execution
  const executeAnimation = useCallback(() => {
    if (!playerRef?.current || !opponentRef?.current) {
      onComplete();
      return;
    }

    storeOriginalStyles();

    // Phase 1: Preparation (150ms)
    if (playerRef.current) {
      playerRef.current.style.transition = 'transform 150ms ease-in-out';
      playerRef.current.style.transform = 'scale(1.05) translateY(-3px)';
    }

    const shakeTimer = setTimeout(() => {
      // Phase 2: Earthquake shake (1000ms)
      if (playerRef.current) {
        playerRef.current.classList.add('earthquake-shake');
      }
      if (opponentRef.current) {
        opponentRef.current.classList.add('earthquake-shake');
        opponentRef.current.classList.add('earthquake-hit');
      }
      
      // Add visual effects
      createGroundEffects();
      createDustClouds();
      
      const endTimer = setTimeout(() => {
        // Phase 3: Settle and cleanup
        if (playerRef.current) {
          playerRef.current.classList.remove('earthquake-shake');
          playerRef.current.style.transform = '';
        }
        if (opponentRef.current) {
          opponentRef.current.classList.remove('earthquake-shake', 'earthquake-hit');
        }
        
        setTimeout(() => {
          cleanup();
          onComplete();
        }, 200);
        
      }, 1000);
      
      timersRef.current.push(endTimer);
    }, 150);
    
    timersRef.current.push(shakeTimer);
  }, [playerRef, opponentRef, onComplete, storeOriginalStyles, createGroundEffects, createDustClouds, cleanup]);

  // Start animation on mount
  useEffect(() => {
    executeAnimation();
    
    // Cleanup on unmount
    return () => {
      cleanup();
    };
  }, [executeAnimation, cleanup]);

  return null; // This component only handles DOM manipulation
}
